import { Card, Checkbox, notification, Space, Typography } from 'antd';
import { useIntl } from 'react-intl';

import {
  FeatureFlag,
  FeatureFlagType,
} from 'protocol/model/dto_organization_pb';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import { userModel } from '@/entities/user';

import { getFeatureFlagLabel } from '../../lib';

import styles from './FeatureFlags.module.scss';

const { Title } = Typography;

export const FeatureFlags = () => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const loading = useAppSelector(
    organizationModel.selectors.selectOrganizationLoading,
  );
  const organization = useAppSelector(
    organizationModel.selectors.selectOrganization,
  );
  const featureFlags = organization?.FeatureFlags;
  const organizationPermissions = organization?.YourPermissions as unknown as
    | Record<string, boolean>
    | undefined;
  const canViewFeatureFlags =
    organizationPermissions?.CanViewFeatureFlags ?? false;

  if (!canViewFeatureFlags) {
    return null;
  }

  const setFeatureFlag = async (featureFlag: FeatureFlag, value: boolean) => {
    try {
      await dispatch(
        userModel.thunks.setOrganizationFeatureFlag({
          OrganizationID: organization.ID,
          FeatureFlag: featureFlag,
          Value: value,
        }),
      ).unwrap();
    } catch (e) {
      notification.error({
        message: formatMessage({
          id: 'error.cannotSetFeatureFlag',
          defaultMessage: "Can't set feature flag",
        }),
      });
    }
  };

  return (
    <Space direction="vertical" size="large" className={styles.container}>
      <Card
        className={styles.card}
        title={
          <Title level={3} className={styles.groupTitle}>
            {formatMessage({
              id: 'organization.featureFlags',
              defaultMessage: 'Feature flags',
            })}
          </Title>
        }
        loading={loading !== 'succeeded'}
        bordered={false}
      >
        <div className={styles.horizontalGroups}>
          <Space direction="vertical" size="small" className={styles.flagGroup}>
            <Title level={4} className={styles.groupTitle}>
              {formatMessage({
                id: 'organization.featureFlags.group.regulatoryRestrictions',
                defaultMessage: 'Regulatory restrictions',
              })}
            </Title>
            {/* TODO: If need separate by groups and backend will add field FeatureGroupType need to make new separation */}
            {featureFlags
              ?.filter(
                ({ Type }) =>
                  Type === FeatureFlagType.FeatureFlagType_Regulatory,
              )
              ?.map(({ Flag, Value, ReadOnly }) => (
                <Checkbox
                  key={Flag}
                  disabled={ReadOnly}
                  checked={Value}
                  onChange={() => setFeatureFlag(Flag, !Value)}
                >
                  {getFeatureFlagLabel(Flag, formatMessage)}
                </Checkbox>
              ))}
          </Space>

          <Space direction="vertical" size="small" className={styles.flagGroup}>
            <Title level={4} className={styles.groupTitle}>
              {formatMessage({
                id: 'organization.featureFlags.group.development',
                defaultMessage: 'Feature flags for development',
              })}
            </Title>

            {featureFlags
              ?.filter(
                ({ Type }) =>
                  Type === FeatureFlagType.FeatureFlagType_Development,
              )
              ?.map(({ Flag, Value, ReadOnly }) => (
                <Checkbox
                  key={Flag}
                  checked={Value}
                  disabled={ReadOnly}
                  onChange={() => setFeatureFlag(Flag, !Value)}
                >
                  {getFeatureFlagLabel(Flag, formatMessage)}
                </Checkbox>
              ))}
          </Space>
        </div>

        <Space direction="vertical" size="small" className={styles.flagGroup}>
          <Title level={4} className={styles.groupTitle}>
            {formatMessage({
              id: 'organization.featureFlags.group.settings',
              defaultMessage: 'Settings',
            })}
          </Title>

          {featureFlags
            ?.filter(
              ({ Type }) => Type === FeatureFlagType.FeatureFlagType_Settings,
            )
            ?.map(({ Flag, Value, ReadOnly }) => (
              <Checkbox
                key={Flag}
                checked={Value}
                disabled={ReadOnly}
                onChange={() => setFeatureFlag(Flag, !Value)}
              >
                {getFeatureFlagLabel(Flag, formatMessage)}
              </Checkbox>
            ))}
        </Space>
      </Card>
    </Space>
  );
};
